import { useEffect, useState } from 'react';
import type { PlayEvent, PlayEventParticipant, PlayerRatingSettings } from '@court-sync/shared';
import {
  formatPlayerRating,
  getPlayEventParticipantDisplayName,
  getPlayEventParticipantSinglesPreferenceLabel,
} from '@court-sync/shared';
import { Input } from '@/components/ui';
import { playEventService } from '@/services/playEvent';
import toast from 'react-hot-toast';

interface PlayEventDrawPlayerLineProps {
  participant: PlayEventParticipant;
  event: PlayEvent;
  organizationId: string;
  eventId: string;
  ratingSettings: PlayerRatingSettings | null;
  onUpdated?: () => void;
  compact?: boolean;
  showSinglesPreference?: boolean;
}

export function PlayEventDrawPlayerLine({
  participant,
  event,
  organizationId,
  eventId,
  ratingSettings,
  onUpdated,
  compact = false,
  showSinglesPreference = true,
}: PlayEventDrawPlayerLineProps) {
  const ratingsEnabled = ratingSettings?.enabled ?? false;
  const ratingLabel = ratingSettings?.label ?? 'Rating';
  const preferenceLabel =
    showSinglesPreference
      ? getPlayEventParticipantSinglesPreferenceLabel(event, participant.prefersSingles)
      : participant.prefersSingles
        ? getPlayEventParticipantSinglesPreferenceLabel(event, true)
        : null;
  const [editingRating, setEditingRating] = useState(false);
  const [draftRating, setDraftRating] = useState(
    participant.playerRating != null ? String(participant.playerRating) : ''
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraftRating(participant.playerRating != null ? String(participant.playerRating) : '');
    setEditingRating(false);
  }, [participant.id, participant.playerRating]);

  const canEditRating = ratingsEnabled;
  const displayName = getPlayEventParticipantDisplayName(participant);

  const saveRating = async () => {
    const trimmed = draftRating.trim();
    if (trimmed === '' && participant.playerRating == null) {
      setEditingRating(false);
      return;
    }

    const nextValue = trimmed === '' ? null : Number(trimmed);
    if (trimmed !== '' && !Number.isFinite(nextValue)) {
      toast.error('Enter a valid rating');
      return;
    }
    if (nextValue === participant.playerRating) {
      setEditingRating(false);
      return;
    }

    setSaving(true);
    try {
      const res = await playEventService.updateParticipant(organizationId, eventId, participant.id, {
        playerRating: nextValue,
      });
      if (res.success) {
        toast.success('Rating updated');
        setEditingRating(false);
        onUpdated?.();
      } else {
        toast.error(res.error ?? 'Failed to update rating');
      }
    } finally {
      setSaving(false);
    }
  };

  const ratingDisplay =
    participant.playerRating != null ? formatPlayerRating(participant.playerRating) : null;

  return (
    <div className="min-w-0">
      <p className={`font-medium leading-snug ${compact ? 'text-sm' : ''}`}>
        {displayName}
        {participant.participantType === 'guest' ? (
          <span className="ml-1.5 text-xs font-normal text-muted-foreground">(Guest)</span>
        ) : null}
      </p>
      {(ratingDisplay || canEditRating || preferenceLabel) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {canEditRating ? (
            editingRating ? (
              <span className="inline-flex items-center gap-1">
                <span>{ratingLabel}</span>
                <Input
                  type="number"
                  className="h-7 w-[4.75rem] px-2 text-xs"
                  step={ratingSettings?.step ?? 0.5}
                  min={ratingSettings?.min}
                  max={ratingSettings?.max}
                  placeholder="—"
                  value={draftRating}
                  onChange={(e) => setDraftRating(e.target.value)}
                  onBlur={() => void saveRating()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                      setDraftRating(
                        participant.playerRating != null ? String(participant.playerRating) : ''
                      );
                      setEditingRating(false);
                    }
                  }}
                  disabled={saving}
                  aria-label={`${ratingLabel} for ${displayName}`}
                />
              </span>
            ) : (
              <button
                type="button"
                className="rounded px-1 py-0.5 text-left hover:bg-muted/80 hover:text-foreground"
                onClick={() => setEditingRating(true)}
              >
                {ratingDisplay ? (
                  <>
                    {ratingLabel} <span className="font-medium text-foreground">{ratingDisplay}</span>
                  </>
                ) : (
                  <span className="text-foreground/80">Add {ratingLabel.toLowerCase()}</span>
                )}
              </button>
            )
          ) : ratingDisplay ? (
            <span>
              {ratingLabel} <span className="font-medium text-foreground">{ratingDisplay}</span>
            </span>
          ) : null}
          {preferenceLabel ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground/80">
              {preferenceLabel}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
